import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Tag } from "types";
import { Checkbox, DropdownMenu, Icon, PageLoading, Button } from "components";
import {
  useCurrentUser,
  useGetTemplates,
  useFetchTemplates,
  useFetchTemplateTags,
  useViewport,
} from "utils/hooks";
import AssignmentPreview from "../AssignmentPreview";
import { AssignmentGallery } from "pages/AppPages/TherapistPages/Resources/components/Assignments/components";
import CurrentAssignmentContext from "pages/AppPages/TherapistPages/Resources/components/Assignments/CurrentAssignmentContext";
import SearchIcon from "assets/icons/magnifying-glass.svg";
import LanguageIcon from "assets/icons/earth-americas.svg";

const VIEPORT_9_VALUE = 992;

interface AddAssignmentsBodyProps {
  handleCloseAssignmentModal: () => void;
}

const AddAssignmentsBody = ({
  handleCloseAssignmentModal,
}: AddAssignmentsBodyProps) => {
  const { width } = useViewport();
  const templatesRef = useRef(false);
  const topRef = useRef<HTMLDivElement | null>(null);
  const { allPublishedAssignmentTemplates, privateTemplates } = useGetTemplates(
    {}
  );
  const [currentAssignment, setCurrentAssignment] = useState();
  const { user } = useCurrentUser();
  const { t } = useTranslation(["common"]);
  const { fetchPublicTemplates, fetchPrivateTemplates } = useFetchTemplates();
  const { fetchAllPublicTemplateTags, fetchAllPrivateTemplateTags } =
    useFetchTemplateTags();

  const [showAssignmentPreview, setShowAssignmentPreview] = useState(false);

  const customAssignmentLimit =
    user?.activeOrganization?.subscription?.plan?.customAssignmentLimit || 0;
  const limitCreateAssignments =
    privateTemplates?.length >= customAssignmentLimit;

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [showCustomAssignments, setShowCustomAssignments] = useState(true);
  const [showBrandAssignments, setShowBrandAssignments] = useState(true);
  const [selectedLanguages, setSelectedLanguages] = useState(new Set(["EN"]));
  const [selectedCategories, setSelectedCategories] = useState(
    new Set(["WORKSHEET", "INFOSHEET", "ASSESSMENT"])
  );

  useEffect(() => {
    if (templatesRef.current) return;
    fetchPublicTemplates();
    fetchPrivateTemplates();
    fetchAllPublicTemplateTags();
    fetchAllPrivateTemplateTags();
    templatesRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLanguageSelection = (
    languageCode: string,
    isSelected: boolean
  ) => {
    const newLanguages = new Set(selectedLanguages);
    if (isSelected) {
      newLanguages.add(languageCode.toUpperCase());
    } else {
      newLanguages.delete(languageCode.toUpperCase());
    }
    setSelectedLanguages(newLanguages);
  };

  const handleCategorySelection = (category: string, isSelected: boolean) => {
    const newCategories = new Set(selectedCategories);
    if (isSelected) {
      newCategories.add(category.toUpperCase());
    } else {
      newCategories.delete(category.toUpperCase());
    }
    setSelectedCategories(newCategories);
  };

  const languageFilter = (assignment: any) => {
    if (selectedLanguages.size === 0) return false;
    const languageTags = assignment.tags?.filter(
      (tag: Tag) => tag.type === "language"
    );
    if (!languageTags || languageTags.length === 0) {
      return selectedLanguages.has("EN");
    }
    return languageTags.some((tag: Tag) =>
      selectedLanguages.has(tag.title.toUpperCase())
    );
  };

  const categoryFilter = (assignment: any) => {
    if (selectedCategories.size === 0) return false;
    return selectedCategories.has(assignment.category?.toUpperCase());
  };

  let filteredPublishedAssignments = allPublishedAssignmentTemplates?.filter(
    (assignment) => {
      return (
        (assignment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          assignment.tags?.some((tag: Tag) =>
            tag.title.toLowerCase().includes(searchTerm.toLowerCase())
          )) &&
        languageFilter(assignment) &&
        categoryFilter(assignment)
      );
    }
  );

  if (!showBrandAssignments) {
    filteredPublishedAssignments = filteredPublishedAssignments?.filter(
      (assignment) =>
        assignment.authorId !== process.env.REACT_APP_TEMPLATE_MASTER_ID
    );
  }

  if (!showCustomAssignments) {
    filteredPublishedAssignments = filteredPublishedAssignments?.filter(
      (assignment) => assignment.authorId !== user?.id
    );
  }

  const handleShowAssignmentPreview = () => {
    if (width <= VIEPORT_9_VALUE) {
      setShowAssignmentPreview(true);
    }
  };

  useEffect(() => {
    if (showAssignmentPreview) {
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [showAssignmentPreview]);

  return (
    <>
      {filteredPublishedAssignments ? (
        <CurrentAssignmentContext.Provider
          value={{ currentAssignment, setCurrentAssignment }}
        >
          <div className={`bottom-modal_body flex space-between`}>
            <div className="w-100 bottom-modal_library">
              <div className="flex align-center justify-between assignment-galler__filters">
                <div className="flex align-center justify-between filters-wrapper">
                  <div className="assignment-gallery__search-wrapper">
                    <div className="assignment-gallery__search-icon">
                      <Icon src={SearchIcon} />
                    </div>
                    <input
                      className="assignment-gallery__search-field"
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="mr-1">
                    <Checkbox
                      name="private_assignments"
                      label={t("resources.my_assignments_filter")}
                      ariaLabel={t("resources.my_assignments_filter")}
                      checked={showCustomAssignments}
                      onChange={(e) =>
                        setShowCustomAssignments(e.target.checked)
                      }
                      variant="no-margin outlined"
                      disabled={limitCreateAssignments}
                    />
                  </div>
                  <Checkbox
                    name="reflective_assignments"
                    label={t("resources.by_reflective_filter")}
                    ariaLabel={t("resources.by_reflective_filter")}
                    checked={showBrandAssignments}
                    onChange={(e) => setShowBrandAssignments(e.target.checked)}
                    variant="no-margin outlined"
                  />
                  <DropdownMenu
                    title={<Icon src={LanguageIcon} />}
                    extraClass="outlined"
                  >
                    <div>
                      <Checkbox
                        name="english_language"
                        label={`English (US)`}
                        ariaLabel={`English (US)`}
                        checked={selectedLanguages.has("EN")}
                        onChange={(e) =>
                          handleLanguageSelection("EN", e.target.checked)
                        }
                        variant="small-margin"
                      />
                    </div>
                    <div>
                      <Checkbox
                        name="hebrew_language"
                        label={`עברית`}
                        ariaLabel={`עברית`}
                        checked={selectedLanguages.has("HE")}
                        onChange={(e) =>
                          handleLanguageSelection("HE", e.target.checked)
                        }
                        variant="small-margin"
                      />
                    </div>
                    <div>
                      <Checkbox
                        name="spanish_language"
                        label={`Español`}
                        ariaLabel={`Español`}
                        checked={selectedLanguages.has("ES")}
                        onChange={(e) =>
                          handleLanguageSelection("ES", e.target.checked)
                        }
                        variant="small-margin"
                      />
                    </div>
                    <div>
                      <Checkbox
                        name="mandarin_language"
                        label={`中文 (简体)`}
                        ariaLabel={`中文 (简体)`}
                        checked={selectedLanguages.has("ZH")}
                        onChange={(e) =>
                          handleLanguageSelection("ZH", e.target.checked)
                        }
                        variant="small-margin"
                      />
                    </div>
                  </DropdownMenu>
                  <DropdownMenu
                    title={t("resources.filter_by_category")}
                    extraClass="outlined"
                  >
                    <div className="mb-2">
                      <Checkbox
                        name="category_worksheet"
                        label="Worksheets"
                        checked={selectedCategories.has("WORKSHEET")}
                        onChange={(e) =>
                          handleCategorySelection("WORKSHEET", e.target.checked)
                        }
                        variant="small-margin"
                      />
                    </div>
                    <div className="mb-2">
                      <Checkbox
                        name="category_infosheet"
                        label="Infosheets"
                        checked={selectedCategories.has("INFOSHEET")}
                        onChange={(e) =>
                          handleCategorySelection("INFOSHEET", e.target.checked)
                        }
                        variant="small-margin"
                      />
                    </div>
                    <div>
                      <Checkbox
                        name="category_assessment"
                        label="Assessments"
                        checked={selectedCategories.has("ASSESSMENT")}
                        onChange={(e) =>
                          handleCategorySelection(
                            "ASSESSMENT",
                            e.target.checked
                          )
                        }
                        variant="small-margin"
                      />
                    </div>
                  </DropdownMenu>
                </div>
              </div>
              <AssignmentGallery
                assignments={filteredPublishedAssignments}
                isDisplayedInModal={true}
                handleShowAssignmentPreview={handleShowAssignmentPreview}
                selectedAssignment={currentAssignment}
              />
            </div>
            <div
              className={`bottom-modal_form ${
                showAssignmentPreview && "bottom-modal_form__visible"
              }`}
              ref={topRef}
            >
              <AssignmentPreview
                handleCloseAssignmentModal={handleCloseAssignmentModal}
              />
              <Button
                name="bottom-modal_hide-form"
                value={""}
                icon="CloseCross"
                extraClass="bottom-modal_hide-form  bottom-modal_button-close transparent"
                onClick={() => setShowAssignmentPreview(false)}
              />
            </div>
          </div>
        </CurrentAssignmentContext.Provider>
      ) : (
        <PageLoading />
      )}
    </>
  );
};

export default AddAssignmentsBody;
